import { motion, useReducedMotion } from "framer-motion";
import { Github, Linkedin, Mail, ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollToId } from "../lib/scroll";

const HERO_IMG =
  "https://images.unsplash.com/photo-1638864616275-9f0b291a2eb6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGRhcmslMjB0ZXh0dXJlfGVufDB8fHx8MTc5Mjk5NzgxNHww&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const reduce = useReducedMotion();
  const anim = (delay) =>
    reduce
      ? {}
      : {
          initial: { opacity: 0, y: 28 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] },
        };

  return (
    <section id="top" data-testid="hero-section" className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-24">
      {/* Abstract geometric visual, bleeding right */}
      <div className="absolute top-0 right-0 w-[70vw] md:w-[44vw] h-[60vh] md:h-[85vh] pointer-events-none" aria-hidden="true">
        <motion.div
          className="w-full h-full"
          {...(reduce
            ? {}
            : {
                initial: { opacity: 0, scale: 1.04 },
                animate: { opacity: 1, scale: 1 },
                transition: { duration: 1.6, ease: "easeOut" },
              })}
        >
          <img
            src={HERO_IMG}
            alt=""
            className="w-full h-full object-cover opacity-40 md:opacity-50"
            style={{
              maskImage: "linear-gradient(to left, black 30%, transparent 100%), linear-gradient(to top, transparent 2%, black 40%)",
              maskComposite: "intersect",
              WebkitMaskImage: "linear-gradient(to left, black 30%, transparent 100%), linear-gradient(to top, transparent 2%, black 40%)",
              WebkitMaskComposite: "source-in",
            }}
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-l from-transparent via-[#080808]/40 to-[#080808]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24 w-full">
        <motion.p
          data-testid="hero-label"
          className="text-xs uppercase tracking-[0.25em] text-[#0EA5E9] font-medium font-mono-alt"
          {...anim(0.1)}
        >
          Computer Science · Fintech · Engineering · Endurance
        </motion.p>

        <motion.h1
          data-testid="hero-headline"
          className="font-heading text-5xl md:text-6xl lg:text-[5.5rem] tracking-tighter leading-[1.02] font-medium text-[#F9F9F9] mt-8 max-w-4xl"
          {...anim(0.25)}
        >
          Building things that move ideas forward.
        </motion.h1>

        <motion.p
          data-testid="hero-intro"
          className="text-base md:text-lg leading-relaxed text-[#A1A1AA] mt-8 max-w-2xl"
          {...anim(0.4)}
        >
          I’m Virat — a computer science student, developer, and aspiring founder
          exploring the intersection of technology, finance, and real-world
          problem-solving.
        </motion.p>

        <motion.p
          data-testid="hero-subtext"
          className="text-sm md:text-base leading-relaxed text-[#71717A] mt-4 max-w-xl"
          {...anim(0.5)}
        >
          From building fintech concepts to experimenting with engineering and
          testing my limits through sport, I’m interested in seeing how far ideas
          and effort can go.
        </motion.p>

        <motion.div data-testid="hero-ctas" className="flex flex-wrap items-center gap-4 mt-12" {...anim(0.65)}>
          <button
            data-testid="hero-cta-work"
            onClick={() => scrollToId("projects")}
            className="group inline-flex items-center gap-2 bg-[#F9F9F9] text-[#080808] px-7 py-3.5 text-sm font-medium rounded-full hover:bg-[#0EA5E9] transition-colors duration-300"
          >
            Explore My Work
            <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-0.5" />
          </button>
          <button
            data-testid="hero-cta-contact"
            onClick={() => scrollToId("contact")}
            className="inline-flex items-center gap-2 border border-white/20 text-[#F9F9F9] px-7 py-3.5 text-sm font-medium rounded-full hover:border-[#0EA5E9] hover:text-[#0EA5E9] transition-colors duration-300"
          >
            Get in Touch
            <ArrowUpRight size={16} />
          </button>
        </motion.div>

        <motion.div data-testid="hero-socials" className="flex items-center gap-6 mt-10" {...anim(0.8)}>
          <a
            data-testid="hero-github-link"
            href="#"
            aria-disabled="true"
            title="GitHub — link coming soon"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#71717A] hover:text-[#F9F9F9] transition-colors"
          >
            <Github size={15} /> GitHub
          </a>
          <a
            data-testid="hero-linkedin-link"
            href="#"
            aria-disabled="true"
            title="LinkedIn — link coming soon"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#71717A] hover:text-[#F9F9F9] transition-colors"
          >
            <Linkedin size={15} /> LinkedIn
          </a>
          <a
            data-testid="hero-email-link"
            href="mailto:cvirat719@gmail.com"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#71717A] hover:text-[#F9F9F9] transition-colors"
          >
            <Mail size={15} /> Email
          </a>
        </motion.div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24 w-full mt-20 pb-10">
        <div className="h-px w-full bg-white/10" />
      </div>
    </section>
  );
};
